import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './Pages.css';

function SignupPage({ onLogin }) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    verificationCode: ''
  });
  const [error, setError] = useState('');
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationSuccess, setVerificationSuccess] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Validate Hong Kong phone number (starts with 5, 6, 9 and is 8 digits)
  const isValidHKPhoneNumber = (phoneNumber) => {
    const hkPhoneRegex = /^[569]\d{7}$/;
    return hkPhoneRegex.test(phoneNumber);
  };

  // Function to send verification code
  const handleSendVerification = (e) => {
    e.preventDefault();
    
    if (!isValidHKPhoneNumber(formData.phoneNumber)) {
      setError('Please enter a valid Hong Kong mobile number (8 digits, starting with 5, 6, or 9)');
      return;
    }
    
    // In a real app, this would call an API to send an SMS
    // For demo purposes, we'll simulate sending a code
    setError('');
    setVerificationSent(true);
    
    // Mock verification code - in a real app this would be sent to the user's phone
    console.log('Verification code for demo: 123456');
  };

  // Function to verify the code
  const handleVerifyCode = (e) => {
    e.preventDefault();
    
    // In a real app, this would validate against a sent code
    // For demo purposes, we'll accept "123456" as the valid code
    if (formData.verificationCode === '123456') {
      setVerificationSuccess(true);
      setError('');
    } else {
      setError('Invalid verification code. Please try again.');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check phone verification
    if (!verificationSuccess) {
      setError('Please verify your phone number to continue');
      return;
    }
    
    // Check username
    if (!formData.username.trim()) {
      setError('Please enter a username');
      return;
    }
    
    // Basic validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    // Clear any errors
    setError('');
    
    // In a real app, you would send signup data to an API
    // For demo purposes, check if username is "Jason"
    console.log(`User signed up with username: ${formData.username}`);
    
    // Simulate successful signup and auto-login
    onLogin();
    navigate('/'); // Redirect to home page after signup
  };

  return (
    <div className="page auth-page signup-page">
      <div className="auth-container">
        <div className="auth-card">
          <h1>Create Account</h1>
          <p className="auth-subtitle">Join SwapWise HK to buy, sell, and swap tech items</p>
          
          {error && <div className="auth-error">{error}</div>}
          
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input 
                type="text" 
                id="username" 
                name="username"
                className="form-control"
                placeholder="Enter your username (e.g. Jason)" 
                value={formData.username}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input 
                type="email" 
                id="email" 
                name="email"
                className="form-control"
                placeholder="Enter your email" 
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="phoneNumber">Phone Number</label>
              <div className="phone-input-group">
                <div className="country-code">+852</div>
                <input 
                  type="tel" 
                  id="phoneNumber" 
                  name="phoneNumber"
                  className="form-control phone-input"
                  placeholder="e.g. 91234567" 
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  disabled={verificationSuccess}
                  required
                />
                {!verificationSent && (
                  <button 
                    type="button" 
                    className="verification-button"
                    onClick={handleSendVerification}
                  >
                    Get Code
                  </button>
                )}
              </div>
            </div>
            
            {verificationSent && !verificationSuccess && (
              <div className="form-group">
                <label htmlFor="verificationCode">Verification Code</label>
                <div className="verification-code-group">
                  <input 
                    type="text" 
                    id="verificationCode" 
                    name="verificationCode"
                    className="form-control"
                    placeholder="Enter 6-digit code" 
                    value={formData.verificationCode}
                    onChange={handleChange}
                    maxLength={6}
                    required
                  />
                  <button 
                    type="button" 
                    className="verification-button"
                    onClick={handleVerifyCode}
                  >
                    Verify
                  </button>
                </div>
                <p className="verification-help">
                  Didn't receive the code? <button type="button" className="resend-link" onClick={handleSendVerification}>Resend</button>
                </p>
              </div>
            )}
            
            {verificationSuccess && (
              <div className="verification-success">
                ✓ Phone number verified successfully
              </div>
            )}
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input 
                type="password" 
                id="password"
                name="password" 
                className="form-control"
                placeholder="Create a password (min. 6 characters)" 
                value={formData.password}
                onChange={handleChange}
                required
                minLength={6}
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input 
                type="password" 
                id="confirmPassword"
                name="confirmPassword" 
                className="form-control"
                placeholder="Confirm your password" 
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </div>
            
            <div className="form-action">
              <button type="submit" className="auth-button" disabled={!verificationSuccess}>Sign Up</button>
            </div>
          </form>
          
          <div className="auth-footer">
            <p>Already have an account? <Link to="/login" className="auth-link">Login</Link></p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SignupPage; 